/* eslint-disable simple-import-sort/imports */
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { reset } from '../../../../../utils/styled-reset';
import { Button } from '../../../../button/button';
import { DateTimePickerDialog } from '../../../../date-time-picker-dialog/date-time-picker-dialog';
import { Icon } from '../../../../icon/icon';
import { IconPanel } from '../../../../icon-panel/icon-panel';
import { Stack } from '../../../../stack/stack';
import { Switch } from '../../../../switch/switch';
import { useAssetFilterStore } from '../../../state/asset-filter-store';
import { CreatedByDialog } from '../../dialogs/created-by-dialog/created-by-dialog';
import { TagFilterDialog } from '../../dialogs/tag-filter-dialog/tag-filter-dialog';
import { TimelineFilterDialog } from '../../dialogs/timeline-filter-dialog/timeline-filter-dialog';
import { AssetFilter } from '../asset-filter';
import { FilterTable } from './filter-table';
import { ObjectTypeDialog } from '../../dialogs/object-type-dialog/object-type-dialog';
// Removed chips in favor of a tabular layout for better overview
import { useAssetMenuTransition } from '../use-asset-menu-transition';
import { isDefined } from '../../../../../../utils/variables';

type Point2d = { x: number; y: number };

type FilterDialogProps = {
  open: boolean;
  width?: number;
  gap?: number;
};

const DEFAULT_WIDTH = 280;
const DEFAULT_GAP = 8;

const FilterDialog: React.FC<FilterDialogProps> = ({ open, width, gap }) => {
  const { t } = useTranslation('skyviewAssetMenu');
  const [position, setPosition] = React.useState<Point2d>({ x: 0, y: 0 });
  const plusButtonRef = React.useRef<HTMLButtonElement>(null);
  const [menuAnchor, setMenuAnchor] = React.useState<Point2d | null>(null);
  const [isEditCreatedAtOpen, setIsEditCreatedAtOpen] = React.useState(false);
  const [isEditCapturedAtOpen, setIsEditCapturedAtOpen] = React.useState(false);
  const [isEditCreatedByOpen, setIsEditCreatedByOpen] = React.useState(false);
  const [isEditTagOpen, setIsEditTagOpen] = React.useState(false);
  const [isEditTypeOpen, setIsEditTypeOpen] = React.useState(false);
  const [isEditTimelineOpen, setIsEditTimelineOpen] = React.useState(false);

  const dialogWidth = width ?? DEFAULT_WIDTH;
  const dialogGap = gap ?? DEFAULT_GAP;
  const {
    isFiltering,
    applyCreatedAtFilter,
    applyImageCapturedAtFilter,
    applyCreatedByFilter,
    applyAssetTypeFilter,
    applyTagGroupsFilter,
    applyTimelineFilter,
    clearFilters,
    filtersEnabled,
    toggleFiltersEnabled,
    tagGroups,
  } = useAssetFilterStore();

  // formatDate moved to FilterTable

  const computePosition = React.useCallback(() => {
    const assetMenu = document.querySelector('[data-asset-menu-root="true"]');
    if (!(assetMenu instanceof HTMLElement)) {
      return;
    }
    const rect = assetMenu.getBoundingClientRect();
    // Prefer anchoring to the right edge of the collapsable container so the dialog follows when the menu collapses.
    // Coordinates are viewport-relative because Container uses position: fixed.
    let left = rect.right + dialogGap;
    // If there's not enough space on the right, fall back to the left side
    const viewportRight = window.innerWidth;
    if (left + dialogWidth > viewportRight - 8) {
      left = rect.left - dialogWidth - dialogGap;
    }
    const top = rect.top + dialogGap;
    setPosition({ x: left, y: top });
  }, [dialogGap, dialogWidth]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    const onResizeOrScroll = () => computePosition();
    window.addEventListener('resize', onResizeOrScroll);
    window.addEventListener('scroll', onResizeOrScroll, { passive: true });

    const assetMenu = document.querySelector('[data-asset-menu-root="true"]');
    let ro: ResizeObserver | undefined;
    if (assetMenu instanceof HTMLElement && 'ResizeObserver' in window) {
      ro = new ResizeObserver(() => computePosition());
      ro.observe(assetMenu);
    }

    computePosition();

    return () => {
      window.removeEventListener('resize', onResizeOrScroll);
      window.removeEventListener('scroll', onResizeOrScroll);
      if (ro) {
        ro.disconnect();
      }
    };
  }, [open, computePosition]);

  useAssetMenuTransition(computePosition);

  if (!open) {
    return null;
  }

  return (
    <Container
      style={{ left: `${position.x}px`, top: `${position.y}px`, width: `${dialogWidth}px` }}
    >
      <Header direction="row" justifyContent="space-between" spacing={1}>
        <Title>{t('topArea.filter.dialogTitle', { ns: 'skyviewAssetMenu' })}</Title>
        <HeaderActions direction="row" spacing={1}>
          <Switch checked={filtersEnabled} size={0.7} onChange={() => toggleFiltersEnabled()} />
          <IconButton
            aria-label={t('topArea.filter.addFilter', { ns: 'skyviewAssetMenu' })}
            ref={plusButtonRef}
            title={t('topArea.filter.addFilter', { ns: 'skyviewAssetMenu' })}
            onClick={(e) => {
              e.stopPropagation();
              if (menuAnchor != null) {
                setMenuAnchor(null);
                return;
              }
              if (plusButtonRef.current) {
                const rect = plusButtonRef.current.getBoundingClientRect();
                setMenuAnchor({ x: rect.right + 4, y: rect.top });
              }
            }}
          >
            <Icon icon={['fal', 'plus']} />
          </IconButton>
        </HeaderActions>
      </Header>

      <Body>
        {!isFiltering && (
          <IconPanel
            header={<Icon icon={['fad', 'filter-circle-xmark']} opacity="50%" size="4x" />}
            responsiveness={{ compressed: false }}
          >
            {{
              title: t('topArea.filter.emptyTitle', { ns: 'skyviewAssetMenu' }),
              info: t('topArea.filter.emptyInfo', { ns: 'skyviewAssetMenu' }),
            }}
          </IconPanel>
        )}

        {isFiltering && (
          <FilterTable
            onEditCreatedAt={() => setIsEditCreatedAtOpen(true)}
            onEditCreatedBy={() => setIsEditCreatedByOpen(true)}
            onEditImageCapturedAt={() => setIsEditCapturedAtOpen(true)}
            onEditTags={() => setIsEditTagOpen(true)}
            onEditTimeline={() => setIsEditTimelineOpen(true)}
            onEditType={() => setIsEditTypeOpen(true)}
          />
        )}
      </Body>

      {isFiltering && (
        <Footer>
          <Stack direction="row-reverse" spacing={0.5}>
            <Button
              color="error"
              title={t('topArea.filter.clearAll', { ns: 'skyviewAssetMenu' })}
              type="button"
              variant="contained"
              onClick={() => clearFilters()}
            >
              {t('topArea.filter.clearAll', { ns: 'skyviewAssetMenu' })}
            </Button>
          </Stack>
        </Footer>
      )}

      {/* Keep AssetFilter mounted to use its context menu only */}
      <Hidden>
        <AssetFilter
          openMenuAt={menuAnchor}
          openMenuTriggerRef={plusButtonRef}
          title={t('topArea.filter.dialogTitle', { ns: 'skyviewAssetMenu' })}
          onClick={() => undefined}
          onMenuClose={() => setMenuAnchor(null)}
        />
      </Hidden>

      {isEditCreatedAtOpen && (
        <DateTimePickerDialog
          displayTime={false}
          endDate={new Date()}
          maxDate={new Date()}
          range={true}
          startDate={new Date()}
          onCancel={() => setIsEditCreatedAtOpen(false)}
          onDateChoosen={() => undefined}
          onRangeChoosen={(from, to) => {
            const startOfDay = new Date(from);
            startOfDay.setHours(0, 0, 0, 0);
            const endOfDay = new Date(to);
            endOfDay.setHours(23, 59, 59, 999);
            applyCreatedAtFilter({
              createdAtFrom: startOfDay.toISOString(),
              createdAtTo: endOfDay.toISOString(),
            });
            setIsEditCreatedAtOpen(false);
          }}
        />
      )}

      {isEditCapturedAtOpen && (
        <DateTimePickerDialog
          displayTime={false}
          endDate={new Date()}
          maxDate={new Date()}
          range={true}
          startDate={new Date()}
          onCancel={() => setIsEditCapturedAtOpen(false)}
          onDateChoosen={() => undefined}
          onRangeChoosen={(from, to) => {
            const startOfDay = new Date(from);
            startOfDay.setHours(0, 0, 0, 0);
            const endOfDay = new Date(to);
            endOfDay.setHours(23, 59, 59, 999);
            applyImageCapturedAtFilter({
              imageCapturedAtFrom: startOfDay.toISOString(),
              imageCapturedAtTo: endOfDay.toISOString(),
            });
            setIsEditCapturedAtOpen(false);
          }}
        />
      )}

      {isEditCreatedByOpen && (
        <CreatedByDialog
          onClose={(user) => {
            if (user?.id) {
              applyCreatedByFilter({ id: user.id, title: user.email });
            }
            setIsEditCreatedByOpen(false);
          }}
        />
      )}

      {isEditTagOpen && (
        <TagFilterDialog
          tagGroups={tagGroups}
          onClose={(selectedTags) => {
            // undefined => cancel; preserve current filter
            if (!isDefined(selectedTags)) {
              setIsEditTagOpen(false);
              return;
            }
            // empty array => user cleared selection; apply clear
            applyTagGroupsFilter(selectedTags);
            setIsEditTagOpen(false);
          }}
        />
      )}

      {isEditTypeOpen && (
        <ObjectTypeDialog
          onClose={(type) => {
            if (type) {
              applyAssetTypeFilter(type);
            }
            setIsEditTypeOpen(false);
          }}
        />
      )}

      {isEditTimelineOpen && (
        <TimelineFilterDialog
          onClose={(timeline) => {
            if (timeline?.id) {
              applyTimelineFilter(timeline.id);
            }
            setIsEditTimelineOpen(false);
          }}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  ${reset}
  --padding: 1em;

  position: fixed;
  z-index: 5;
  background-color: #fff;
  box-shadow: 2px 2px 8px 0 #00000060;
  border-radius: 5px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const Header = styled(Stack)`
  background-color: #eee;
  padding: var(--padding);
`;

const Title = styled.span`
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const HeaderActions = styled(Stack)`
  margin-left: auto;
`;

const IconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
`;

const Body = styled.div`
  padding: 1em;
`;

const Hidden = styled.div`
  display: contents;
`;

// Table cells moved into separate component

// ActionIconButton moved to FilterTable

const Footer = styled.footer`
  background-color: #eee;
  padding: var(--padding);
`;

// FooterButton removed; using shared Button component instead

export { FilterDialog };
